import React, { Fragment, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { z } from 'zod';
import classNames from 'classnames';
import { get, kebabCase } from 'lodash';
import { BLOCKS } from '@contentful/rich-text-types';
import { logError } from '$util/logger';
import { useResizeViewport } from '$util/hooks/useResizeViewport';
import type { SchemaType } from '$util/schema';
import { replaceCollectionPlaceholders } from '$cms/replaceCollectionPlaceholders';
import { useCmsPageContext } from '$cms/CmsPageContext';
import type { ContentComponent } from '$cms/ContentComponent';
import { CmsRichTextSchema, CmsShortTextSchema } from '$cms/types';
import { Accordion } from '$quantum/molecules/Accordion';
import { ContentComponentSection } from '$src-components/atoms/ContentComponentSection';
import { Row, Col } from '$src-components/atoms/Grid';
import type { MakeContentfulRichTextRendererOptions } from '@/cms/richTextContentHelpers';
import type { SectionItem } from './utils/makeSectionItemsFromRichText';
import { makeSectionItemsFromRichText } from './utils/makeSectionItemsFromRichText';
import { chunkRichTextBlocksInSections } from './utils/chunkRichTextBlocksInSections';
import { getTopLevelBlocks } from './utils/getTopLevelBlocks';

import styles from './index.module.scss';

const getHeadingText = (node: { content: unknown[] }) => get(node, 'content[0].value', '');
const sectionItemsRichTextOptions: MakeContentfulRichTextRendererOptions = {
  renderNode: {
    [BLOCKS.HEADING_3]: (node) => {
      const heading = getHeadingText(node);
      return heading && <h3 id={kebabCase(heading)}>{heading}</h3>;
    },
    [BLOCKS.HEADING_4]: (node) => {
      const heading = getHeadingText(node);
      return heading && <h4 id={kebabCase(heading)}>{heading}</h4>;
    },
    [BLOCKS.HEADING_5]: (node) => {
      const heading = getHeadingText(node);
      return heading && <h5 id={kebabCase(heading)}>{heading}</h5>;
    },
  },
};

const FaqCollectionSchema = {
  translatedEntities: z.object({
    title: CmsShortTextSchema.optional(),
    /**
     * Override the content from Collection Items.
     */
    richText: CmsRichTextSchema.optional(),
  }),
};

export type FaqCollectionTranslatedEntities = SchemaType<(typeof FaqCollectionSchema)['translatedEntities']>;

export const FaqCollection: ContentComponent<FaqCollectionTranslatedEntities> = function FaqCollection({
  translatedEntities,
  className,
  ...props
}): JSX.Element | null {
  const cmsPageContext = useCmsPageContext();
  const [mobile, setMobile] = useState(false);
  const { asPath } = useRouter();
  useResizeViewport({
    breakpoint: 'md',
    direction: 'down',
    fn: (isMobile: boolean) => setMobile(isMobile),
  });

  const { title, richText } = translatedEntities;
  const collectionItems = cmsPageContext?.pageRoutes?.[0]?.collectionItems;

  const blocks = useMemo(
    () => getTopLevelBlocks(collectionItems, richText?.content),
    [richText?.content, collectionItems]
  );

  /**
   * Accordion items grouped by H3 headings.
   */
  const sectionItems: SectionItem[] = useMemo(() => {
    const [richTexts, orphanBlocks] = chunkRichTextBlocksInSections(blocks);
    if (orphanBlocks?.length) {
      /**
       * @note When this happens look at `richText` translated entity (if it's set)
       * or at the CollectionItems used by the page and identify which content doesn't start with a section heading.
       */
      logError('Found RichText sections that do not start with heading', {
        pathname: asPath,
        orphanBlocks: orphanBlocks.length,
      });
    }
    const items = makeSectionItemsFromRichText(richTexts, sectionItemsRichTextOptions);
    return items;
  }, [asPath, blocks]);

  /**
   * On desktop, all heading sizes are listed in sidebar.
   */
  const desktopHeadings = useMemo(
    () =>
      blocks.flatMap(({ nodeType, content: [text] }) =>
        nodeType.startsWith('heading-') && text.nodeType === 'text' ? text.value : []
      ),
    [blocks]
  );
  if (!sectionItems.length) {
    logError(
      `${FaqCollection.displayName}: Invalid sections data configuration, the component will not display!`,
      {
        pathname: asPath,
      }
    );
    return null;
  }
  return (
    <ContentComponentSection
      className={classNames(FaqCollection.displayName, styles.component, className)}
      {...props}
    >
      <Row className={mobile ? 'mobile' : 'desktop'}>
        {title?.value && (
          <Col>
            <h2>{replaceCollectionPlaceholders(title.value, collectionItems)}</h2>
          </Col>
        )}
        {mobile ? (
          <Col>
            <Accordion accordionItems={sectionItems} />
          </Col>
        ) : (
          <>
            <Col large={4} className="menu-col">
              <aside>
                {desktopHeadings.map((heading) => {
                  const hId = kebabCase(heading);
                  return (
                    <a key={hId} href={`#${hId}`}>
                      {heading}
                    </a>
                  );
                })}
              </aside>
            </Col>
            <Col large={7} largeStart={6}>
              {sectionItems.map(({ content }) => content)}
            </Col>
          </>
        )}
      </Row>
    </ContentComponentSection>
  );
};

FaqCollection.displayName = 'FaqCollection';
FaqCollection.schema = FaqCollectionSchema;
